import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db, storage, auth } from '../../../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { 
  AppBar, Box, Typography, Button, IconButton, Grid, Snackbar, Tooltip, Card, CardContent, CardActions, MenuItem, Select, FormControl, InputLabel, Dialog, DialogActions, DialogContent, Tabs, Tab, DialogTitle 
} from '@mui/material';
import { Delete, Edit, Add, Undo } from '@mui/icons-material';
import { Fab } from '@mui/material';
import { deleteObject, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Login from '../../common/Login';
import '../../../styles/EditMenuPage.scss';
import Header from '../../common/Header';
import Arrow from '../../common/Arrow';
import { isBeer, isWine, isBurger } from '../../common/utils';
import GenericFormGroup from './forms/GenericFormGroup';
import BeerFormGroup from './forms/BeerFormGroup';
import * as Icons from '../../../assets/images/allergens/AllergenIcons';
import WineManager from './forms/WineManager';
import BurgerManager from './forms/BurgerManager';
import {TextField} from '@mui/material';
import EditSection from './EditSection';
import AdministratorTabPage from './AdministratorTabPage';

const EditMenuPage = () => {
  const [menuSections, setMenuSections] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentSection, setCurrentSection] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', description: '', price: '', image: null, allergens: [], prices: [], imageUrl: '' });
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [undoAction, setUndoAction] = useState(null);
  const [user, setUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allergensList] = useState(['Glutine', 'Uova', 'Arachidi', 'Lattosio', 'Crostacei', 'Pesce', 'Soia', 'Frutta a guscio', 'Sedano', 'Senape', 'Semi di sesamo', 'Anidride solforosa', 'Lupini', 'Molluschi']);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const handleAllergensChange = (event) => {
    const { target: { value } } = event;
    setSelectedAllergens(typeof value === 'string' ? value.split(',') : value);
  };

  useEffect(() => {
    const fetchMenuSections = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'sections'));
        const sections = {};
        
        snapshot.forEach(doc => {
          const data = doc.data();
          const section = data.name;
          sections[section] = sections[section] || [];
          sections[section].push({
            id: doc.id,
            ...data,
            items: data.items.map(item => (
              isBeer(section) ? { ...item, prices: item.prices || [], imageUrl: item.imageUrl || '' } : item
            ))
          });
        });

        const orderedMenuSections = Object.keys(sections).sort((a, b) => sections[a][0].order - sections[b][0].order)
          .map(section => ({
            sectionName: section,
            subtitle: sections[section][0].subtitle,
            order: sections[section][0].order,
            items: sections[section][0].items,
            id: sections[section][0].id
          }));

        setMenuSections(orderedMenuSections);
        setSelectedTab(orderedMenuSections?.indexOf(orderedMenuSections?.find(section => isBeer(section.sectionName))) || 0);
        setCurrentSection(orderedMenuSections?.find(section => isBeer(section.sectionName)) || orderedMenuSections[0]); 
      } catch (error) {
        console.error('Error fetching menu items:', error);
      }
    };

    fetchMenuSections();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleSelectChange = (event) => {
    const newIndex = event.target.value;
    setSelectedTab(newIndex);
    setCurrentSection(menuSections[newIndex]);
    setSelectedAllergens([]);
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const handleEditItem = (item) => {
    setNewItem(item);
    setSelectedAllergens(item.allergens || []);
    setDialogOpen(true); // Apri il popup
  };

  const MemoizedCard = React.memo(({ item, handleEditItem, handleDeleteItem }) => (
    <Grid item xs={12} md={6} key={item.id}>
      <Card>
        <CardContent>
            <div className='mui-card-header'>
              {item.imageUrl && <img src={item.imageUrl} alt={item.name} className="item-image" loading="lazy" />}
              <Typography variant="h6">{item.name}</Typography>

              {isBeer(menuSections[selectedTab]?.sectionName) ? (
                <div className='prices'>
                {item.prices.map((priceObj, i) => (
                    <Typography key={`${item.id}-${i}`} variant="body2">{priceObj.size} {priceObj.price}</Typography>
                ))}
                </div>
              ) : (
                <Typography variant="body2">{item.price}€</Typography>
              )}
            </div>
            <Typography variant="body2">{item.description}</Typography>
        </CardContent>
        <CardActions>
          <div className="allergen-icons">
              {item.allergens?.map((allergen, index) => (
                <div className="allergen-icon">
                    {Icons.findAllergenComponent(allergen)}
                </div>
              ))}
          </div>
          <div>
            <Tooltip title="Edit">
              <IconButton onClick={() => handleEditItem(item)}>
                  <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton onClick={() => handleDeleteItem(menuSections[selectedTab]?.sectionName, item.id)}>
                  <Delete />
              </IconButton>
            </Tooltip>
          </div>
        </CardActions>
      </Card>
  </Grid>
 ));

  const handleDeleteItem = async (sectionNameParam = null, itemIdParam = null) => {
    const sectionName = sectionNameParam || itemToDelete.sectionName;
    const itemId = itemIdParam || itemToDelete.itemId;
    try {
      const sectionDocs = await getDocs(collection(db, 'sections'));
      sectionDocs.forEach(async (doc) => {
        if (doc.data().name === sectionName) {
          const sectionData = doc.data();
          const item = sectionData.items.find(item => item.id === itemId);
          if (item && item.imageUrl) {
            const imageRef = ref(storage, item.imageUrl);
            await deleteObject(imageRef);
          }
          sectionData.items = sectionData.items.filter(item => item.id !== itemId);
          await updateDoc(doc.ref, sectionData);
        }
      });
      const updatedSections = menuSections.map(section => (
        section.sectionName === sectionName 
          ? { ...section, items: section.items.filter(item => item.id !== itemId) } 
          : section
      ));
      setMenuSections(updatedSections);
      handleConfirmDeleteClose();
      showSnackbar('Elemento eliminato con successo!');
    } catch (error) {
      console.error('Error deleting item:', error);
      showSnackbar('Error deleting item');
    }
  };

  const addItem = async (section, item = null) => {
    const itemData = item || newItem;
    if ((!(isWine(section.sectionName) || isBurger(section.sectionName)) && itemData.name.trim() === "") || (isBeer(section.sectionName) && itemData.prices.length === 0)) return;
    itemData.allergens = selectedAllergens;
    const file = newItem.image;
    itemData.image = null;
    
    try {
      let updatedItems;
      const sectionRef = doc(db, 'sections', section.id);
      
      if (file) {
        const storageRef = ref(storage, `images/${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        itemData.imageUrl = downloadURL;
      }
      if (itemData.id) {
        updatedItems = section.items.map(item => (item.id === itemData.id ? { ...itemData } : item));
      } else {
        const newItemWithId = { ...itemData, id: Date.now().toString() };
        if (isWine(section.sectionName)) {
          newItemWithId.wines = [];
        } else if (isBurger(section.sectionName)) {
          newItemWithId.burgers = [];
        }
        updatedItems = [...section.items, newItemWithId];
      }
      await updateDoc(sectionRef, { items: updatedItems });
      const updatedSections = menuSections.map(sec => (sec.id === section.id ? { ...sec, items: updatedItems } : sec));
      setMenuSections(updatedSections);

      cancelItem();
      showSnackbar('Elemento aggiunto con successo');
    } catch (error) {
      console.error('Error adding item:', error);
      showSnackbar('Si è verificato un errore');
    }
  };

  const cancelItem = () => {
    setNewItem({ name: "", description: "", price: "", allergens: [], image: null, prices: [], imageUrl: '' });
    setSelectedAllergens([]);
    setDialogOpen(false); // Chiudi il popup
  };

  const showSnackbar = (message, action = null) => {
    setSnackbarMessage(message);
    setUndoAction(action);
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true); // Apri il popup
  };

  const handleDialogClose = () => {
    setDialogOpen(false); // Chiudi il popup
  };

  const handleOpenConfirmDelete = (sectionName, itemId) => {
    setItemToDelete({sectionName, itemId});
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setItemToDelete(null);
    setConfirmDeleteOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const updateSections = async (sectionsArray) => {
    try {
      const snapshot = await getDocs(collection(db, 'sections')); // Recupera tutti i documenti in una sola chiamata
      const existingIds = new Set(snapshot.docs.map(doc => doc.id)); // Crea un set degli id esistenti
  
      // const batch = db.batch();
      const batch = writeBatch(db);
  
      for (const section of sectionsArray) {
        const sectionRef = doc(db, 'sections', section.id); // Riferimento al documento con l'id specificato
        
        if (section.deleted) {
          if (existingIds.has(section.id)) {
            batch.delete(sectionRef);
          }
        } else {
          const sectionData = {
            name: section.name,
            subtitle: section.subtitle ?? "",
            order: section.order
          };
  
          if (existingIds.has(section.id)) {
            // Se l'id esiste nel set, aggiorna il documento
            batch.update(sectionRef, sectionData);
          } else {
            // Se l'id non esiste, inserisci un nuovo documento
            batch.set(sectionRef, { ...sectionData, items: [] } );
          }
        }
      }
  
      await batch.commit();

      const existingSectionsIds = new Set(menuSections.map(s => s.id));
      const _sections = sectionsArray.filter(section => !section.deleted)
                                      .map(section => ({
                                        ...section, 
                                        sectionName: section.name, 
                                        items: existingSectionsIds.has(section.id) ? 
                                                    menuSections[menuSections?.indexOf(menuSections?.find(s => s.id === section.id))].items 
                                                    : []
                                      }));
      setMenuSections(_sections.sort((a, b) => a.order - b.order) ); // Aggiorna lo stato locale
      showSnackbar('Sezioni aggiornate con successo');
    } catch (error) {
      console.error('Errore durante l\'aggiornamento delle sezioni:', error);
      showSnackbar('Si è verificato un errore durante l\'aggiornamento');
    }
  };
  

  if (!user) {
    return <Login />;
  }

  return (
    <div>
      <Header>
        <div className='header-child menu-title'>
          <h1>Pannello Admin</h1>
          <p>Benvenuto nel pannello di amministrazione! Da qui sarà possibile modificare le varie sezioni del menù</p>
          <Arrow direction="down" size="small" animated={true}/>
        </div>
      </Header>
      {/* {(!menuSections || menuSections.length === 0) && <div color='white'>Loading...</div>} */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} centered TabIndicatorProps={{
            style: {
              backgroundColor: '#FAA916',
            },
          }}>
          <Tab label="Menù" />
          <Tab label="Sezioni" />
          <Tab label="Amministratore" />
        </Tabs>
      </Box>

      {tabIndex === 0 ? (
        <div className="edit-menu-page">
          <AppBar position="static" className="custom-app-bar">
            {/* <Button className="logout-button" onClick={handleLogout}>Logout</Button> */}
            <FormControl variant="outlined"sx={{
                width: '100%',
                '.MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'white' }, // Border bianco
                  '&:hover fieldset': { borderColor: 'white' }, // Hover border bianco
                  '&.Mui-focused fieldset': { borderColor: 'white' } // Focus border bianco
                },
                '.MuiInputLabel-root': {
                  color: 'white', // Colore etichetta normale
                },
                '.MuiInputLabel-root.Mui-focused': {
                  color: 'white', // Colore etichetta in focus
                }
              }}
            >
            <InputLabel sx={{ color: 'white' }}>Seleziona una sezione</InputLabel>
              <Select
                value={selectedTab}
                onChange={handleSelectChange}
                label="Seleziona una sezione"
                sx={{ color: 'white' }}
              >
                {menuSections.map((section, index) => (
                  <MenuItem key={section.id} value={index}>
                    {section.sectionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </AppBar>

          {/* Fab button */}
          <Fab color="primary" aria-label="add" onClick={handleDialogOpen} className="fab-button new-btn">
            <Add />
          </Fab>

          <Box p={3} className="section-content">
            {menuSections[selectedTab] && !(isWine(menuSections[selectedTab].sectionName) || isBurger(menuSections[selectedTab].sectionName)) && (
              <Grid container spacing={3}>
              {menuSections[selectedTab].items.map((item, index) => (
                <MemoizedCard
                  key={item.id} // Assicurati di avere una chiave univoca qui.
                  item={item}
                  handleEditItem={handleEditItem}
                  handleDeleteItem={handleOpenConfirmDelete}
                />
              ))}
            </Grid>
            
            )}
            {menuSections[selectedTab] && isWine(menuSections[selectedTab].sectionName) && (
              <div className='MuiCardContent-root'>
                  <WineManager
                    sectionId={menuSections[selectedTab]?.id}
                    handleEditItem={handleEditItem}
                    winesSections={menuSections[selectedTab]?.items}
                    setWinesSections={(updatedWines, isDeletion = false, itemId = null, item = null) => {
                      const updatedSections = menuSections.map((sec) =>
                        sec.id === menuSections[selectedTab]?.id ? { ...sec, items: updatedWines } : sec
                      );
                      setMenuSections(updatedSections);
                      menuSections[selectedTab].items = updatedWines;
                      if (!isDeletion) {
                        addItem(menuSections[selectedTab], item);
                      } else {
                        handleDeleteItem(menuSections[selectedTab].sectionName, itemId);
                      }
                    }}
                  />
              </div>
            )}
            {menuSections[selectedTab] && isBurger(menuSections[selectedTab].sectionName) && (
              <div className='MuiCardContent-root'>
                  <BurgerManager
                    sectionId={menuSections[selectedTab].id}
                    handleEditItem={handleEditItem}
                    burgersSections={menuSections[selectedTab].items}
                    setBurgersSections={(updatedItems, isDeletion = false, itemId = null, item = null) => {
                      const updatedSections = menuSections.map((sec) =>
                        sec.id === menuSections[selectedTab].id ? { ...sec, items: updatedItems } : sec
                      );
                      setMenuSections(updatedSections);
                      menuSections[selectedTab].items = updatedItems;
                      if (!isDeletion) {
                        addItem(menuSections[selectedTab], item);
                      } else {
                        handleDeleteItem(menuSections[selectedTab].sectionName, itemId);
                      }
                    }}
                  />
              </div>
            )}
          </Box>

          {/* Dialog per il form di inserimento/modifica */}
          <Dialog open={dialogOpen} onClose={handleDialogClose}>
            <DialogTitle>{newItem.id ? 'Modifica Item' : 'Aggiungi Item'}</DialogTitle>
            <DialogContent>
              {menuSections[selectedTab] && isWine(menuSections[selectedTab].sectionName) ? (
                  <TextField
                    label="Nome Cantina"
                    fullWidth
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    margin="normal"
                  />
              ) : (menuSections[selectedTab] && isBurger(menuSections[selectedTab].sectionName) ? (
                <TextField
                    label="Nome Sezione"
                    fullWidth
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    margin="normal"
                  />
              ) : (
                <>
                  <GenericFormGroup
                    newItem={newItem}
                    setNewItem={setNewItem}
                    allergensList={allergensList}
                    selectedAllergens={selectedAllergens}
                    handleAllergensChange={handleAllergensChange}
                    multiplePrices={isBeer(menuSections[selectedTab]?.sectionName)}
                  />
                  {menuSections[selectedTab] && isBeer(menuSections[selectedTab].sectionName) && (
                    <BeerFormGroup newItem={newItem} setNewItem={setNewItem} />
                  )}
                </>)
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => addItem(menuSections[selectedTab])} className="add-btn">
                {newItem.id ? 'Aggiorna' : 'Aggiungi'}
              </Button>
              <Button onClick={cancelItem} className="cancel-btn">Annulla</Button>
            </DialogActions>
          </Dialog>
          
          {/* Dialog di conferma per la cancellazione */}
          <Dialog open={confirmDeleteOpen} onClose={handleConfirmDeleteClose}>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogContent>
              <Typography>Sei sicuro di voler eliminare questo elemento?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleDeleteItem()} color="error" variant="contained">
                Elimina
              </Button>
              <Button onClick={handleConfirmDeleteClose} className="cancel-btn" color="secondary">Annulla</Button>
            </DialogActions>
          </Dialog>
        </div>
      ) : tabIndex === 1 ? (<EditSection sections={menuSections} setSections={updateSections}/>) 
          : (
            <AdministratorTabPage></AdministratorTabPage>
          )}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={undoAction && (
          <Button color="secondary" size="small" onClick={undoAction}>
            Undo
          </Button>
        )}
      />
    </div>
  );
};

export default EditMenuPage;
